import { CallDto, UserDto } from '@act/shared/data-transfer-objects';
import { useMemo } from 'react';
import { utilityAppSelectors } from 'redux/utils/selectors/utilitySelectors';

export interface MinimizedCallBannerPresentation {
  callTitle: string;
  phoneLabel: string;
  subjectUser: UserDto | undefined;
}

/**
 * Derives minimized banner labels and the member user for restoring the call dialog.
 *
 * @param call - Active minimized call, if any.
 * @returns Banner copy and subject user for {@link useInteractionsDialog.editCall}.
 */
export function useMinimizedCallBannerPresentation(
  call: CallDto | undefined
): MinimizedCallBannerPresentation {
  const subjectUser = utilityAppSelectors.selectUserOrMemberById(
    call?.subjectId ?? null
  );

  const isResourceForPanels =
    !!call?.needResourceInteraction?.resource?.title ||
    !!call?.resourceInteraction?.resource?.title;

  const callTitle = useMemo(() => {
    if (!call) {
      return '';
    }
    if (isResourceForPanels) {
      return (
        call.needResourceInteraction?.resource?.title ??
        call.resourceInteraction?.resource?.title ??
        'Resource call'
      );
    }
    return `${subjectUser?.firstName ?? ''} ${subjectUser?.lastName ?? ''}`.trim();
  }, [call, isResourceForPanels, subjectUser?.firstName, subjectUser?.lastName]);

  const phoneLabel = useMemo(() => {
    if (!call) {
      return '';
    }
    if (isResourceForPanels) {
      return (
        call.receiver?.phoneNumber ?? call.originator?.phoneNumber ?? ''
      );
    }
    const memberNumber =
      call.receiver?.userId === subjectUser?.id
        ? call.receiver?.phoneNumber
        : call.originator?.phoneNumber;
    return memberNumber ?? '';
  }, [call, isResourceForPanels, subjectUser?.id]);

  return {
    callTitle,
    phoneLabel,
    subjectUser,
  };
}
